import React, { useCallback, useContext, useMemo, useState } from 'react'
import { makeStyles } from 'tss-react/mui'
import { ResponsiveContext } from '../../utils/context/ResponsiveContext'
import { RechercheContext } from './utils/RechercheContext'
import { FormattedMessage, useIntl } from 'react-intl'
import AutreElementCrossSellingCard from '../../components/swiper/AutreElementCrossSellingCard'
import { isEmpty } from 'lodash'
import { fontFamilyMonserrat, GRIS, WHITE } from '../../theme'
import Loader from '../../components/loader/Loader'
import InfiniteScroll from 'react-infinite-scroll-component'
import AutresElementsCrossSelling from '../../components/swiper/AutresElementsCrossSelling'
import styles from '../../styles'
import { objectGetValues } from '../../utils/ieCompatibility'
import { triResultatRechercheEnum } from './enums/triResultatRechercheEnum'
import AucunResultat from './components/AucunResultat'
import linksEnum from '../../enums/linksEnum'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Box, Grid, Input, MenuItem, Select, styled } from '@mui/material'
import { ExpandMore } from '@mui/icons-material'
import { getResultatRecherche } from './api/getResultatRecherche'
import { formatOffreResultatRecherche } from '../../components/swiper/utils/autresElementsCrossSellingUtils'
import { URL_PARAMS_FILTRES } from './utils/rechercheConstants'

const CustomSelect = styled(Select)(({ theme }) => ({
	background: WHITE,
	borderRadius: 42,
	border: '1px solid #D0D5DA',
	width: 300,
	height: 50,
	padding: '0 0 0 25px',
	...styles(theme).text2,
	color: '#8895A1',
	display: 'flex',
	alignItems: 'center',
	lineHeight: '24px',
	'& span': {
		color: theme.palette.blue.main,
		fontWeight: 600,
		marginLeft: 10
	},
	'& .MuiSelect-icon': {
		marginRight: 20
	}
}))

const useStyles = makeStyles<{ isLargeScreen: boolean, afficherCarte: boolean, withMinHeight: boolean }>()(
	(theme, { isLargeScreen, afficherCarte, withMinHeight }) => ({
		...styles(theme),
		resultatsRechercheContainer: {
			background: GRIS,
			paddingBottom: 30
		},
		loaderContainer: {
			minHeight: withMinHeight ? '65vh' : 'unset'
		},
		navigationContainer: {
			padding: isLargeScreen ? `30px ${afficherCarte ? 40 : 65}px 11px 65px` : '47px 30px 37px'
		},
		nombreOffre: {
			padding: isLargeScreen ? `7px ${afficherCarte ? 40 : 65}px 0 65px` : '1px 30px 16px',
			fontSize: isLargeScreen ? 18 : 16,
			fontFamily: fontFamilyMonserrat,
			color: theme.palette.blue.main,
			fontWeight: 500,
			whiteSpace: 'break-spaces'
		},
		boutonSticky: {
			borderRadius: 42,
			height: 50,
			position: 'sticky',
			bottom: isLargeScreen ? 37 : 30,
			left: isLargeScreen ? 'unset' : '50%',
			transform: isLargeScreen ? '' : 'translateX(-50%)',
			zIndex: 1,
			minWidth: 177,
			padding: 0,
			justifyContent: 'space-evenly',
			marginTop: 50
		},
		voirPlus: {
			marginTop: -50,
			background: GRIS,
			zIndex: 1
		},
		mapContainerStyle: {
			position: 'relative',
			height: '82vh',
			padding: 0,
			width: '-webkit-fill-available'
		},
		mapFixed: {
			'& > div:first-child': {
				top: '-20px !important',
				height: '100%'
			}
		},
		mapSticky: {
			position: 'fixed',
			width: '-webkit-fill-available',
			height: '90vh',
			marginTop: -20
		},
		mapAbsolute: {
			position: 'absolute',
			top: 0,
			left: 0,
			width: '100vw',
			height: '100vh'
		},
		infiniteScroll: {
			overflowY: 'hidden !important' as 'hidden',
			display: 'flex',
			flexWrap: 'wrap',
			justifyContent: 'flex-start',
			marginLeft: isLargeScreen ? 0 : 30,
			marginRight: isLargeScreen ? 0 : 30,
			paddingLeft: isLargeScreen ? 50 : 0,
			paddingRight: isLargeScreen ? 50 : 0,
			background: GRIS,
			zIndex: 1,
			position: 'relative',
			marginBottom: 'auto'
		},
		resultatsApprochantsTitre: {
			paddingLeft: isLargeScreen ? 65 : 30,
			paddingRight: isLargeScreen ? 65 : 30,
			...styles(theme).title0,
			fontSize: isLargeScreen ? 35 : 22,
			lineHeight: isLargeScreen ? '55px' : '32px',
			marginTop: isLargeScreen ? 40 : 43,
			marginBottom: isLargeScreen ? 22 : 31,
			whiteSpace: 'break-spaces'
		},
		styleTri: {
			marginTop: isLargeScreen ? 0 : 35
		},
		menuContainer: {
			'& .MuiPaper-root': {
				width: 200,
				borderRadius: 15
			}
		},
		listeContainer: {
			minHeight: isLargeScreen ? 620 : 600
		}
	}))

const ResultatsRecherche = () => {
	const { isLargeScreen } = useContext(ResponsiveContext)
	const { resultat, loading, filtres, setResultat, setOffreSurvolee } = useContext(RechercheContext)

	const afficherCarte = false
	const intl = useIntl()
	const navigate = useNavigate()
	const [searchParams] = useSearchParams()
	const [infiniteScrollIndex, setInfiniteScrollIndex] = useState<number>(0)
	const { classes, cx } = useStyles({ isLargeScreen, afficherCarte, withMinHeight: afficherCarte && resultat.offresApprochantes.length === 0 })

	const offresApprochantes = useMemo(() => resultat.offresApprochantes.map((offreApprochante) =>
		formatOffreResultatRecherche(offreApprochante, intl, 261, 207)
	), [resultat, intl])

	const afficherSelectTrierPar = useCallback(() => {
		const defaultValue = !searchParams.get(URL_PARAMS_FILTRES) ? (triResultatRechercheEnum.disponibilite) : JSON.parse(searchParams.get(URL_PARAMS_FILTRES) as string).tri
		return <CustomSelect
			displayEmpty
			input={<Input disableUnderline={true} />}
			IconComponent={ExpandMore}
			autoWidth
			value={defaultValue}
			className={classes.styleTri}
			onChange={(e) => {
				const filtresRequete = { ...filtres, tri: e.target.value as triResultatRechercheEnum, page: 0 }
				navigate(`${linksEnum.RECHERCHE_OFFRE}?filtres=${JSON.stringify(filtresRequete)}`)
			}}
			renderValue={(selected) => (
				<div>
					<FormattedMessage id="resultatsRecherche.tri.prefix" defaultMessage="Trier par :" description="Prefix du label" />
					<span>
						<FormattedMessage id={`resultatsRecherche.tri.${selected as string}`} defaultMessage="Disponibilité" description="Option du tri du résultat de recherche" />
					</span>
				</div>
			)}
			MenuProps={{ className: classes.menuContainer }}
		>
			{objectGetValues(triResultatRechercheEnum).map((triResultatRecherche) => (
				<MenuItem key={triResultatRecherche} value={triResultatRecherche}>
					<FormattedMessage
						id={`resultatsRecherche.tri.${triResultatRecherche}`}
						defaultMessage="Disponibilité"
						description="Option du tri du résultat de recherche"
					/>
				</MenuItem>
			))}
		</CustomSelect>
	}, [classes, searchParams, filtres, navigate])

	return (
		<Grid id="resultatsRechercheContainer" container item xs={12} justifyContent="center" className={classes.resultatsRechercheContainer}>
			{
				// Si on est en destop OU que l'on est en mobile et que la carte n'est pas affichée, on affiche la liste des offres
				(isLargeScreen || (!isLargeScreen && !afficherCarte)) && (
					<Grid container item xs={12} md={afficherCarte ? 6 : 12} justifyContent="center">
						<Grid container item xs={12} alignItems="center"
						      className={cx(
							      classes.navigationContainer,
							      { 'navigationContainerSmall': !isLargeScreen }
						      )}
						>
							<Grid container item justifyContent="flex-end" alignItems="center">
								{isLargeScreen && afficherSelectTrierPar()}
							</Grid>
						</Grid>

						<Grid container item xs={12} justifyContent="center">
							{loading ?
								<Grid container item xs={12} className={classes.loaderContainer}>
									<Loader />
								</Grid>
								: isEmpty(resultat.offresExactes) ?
									<AucunResultat afficherCarte={afficherCarte} />
									:
									<Grid container item xs={12} direction="column"
									      className={cx({
										      [classes.listeContainer]: offresApprochantes.length === 0
									      })}
									>
										<Grid container item className={classes.nombreOffre} justifyContent="space-between" alignItems="center">
											<div>
												<FormattedMessage
													id="recherche.nombreOffre"
													defaultMessage="<strong>{nb}</strong> biens disponibles"
													description="Number of offers message"
													values={{
														strong: (chunk) => <strong>{chunk}</strong>,
														nb: resultat.totalOffresExactes
													}}
												/>
											</div>
											{!isLargeScreen && afficherSelectTrierPar()}
										</Grid>

										<InfiniteScroll
											dataLength={resultat.offresExactes.length}
											next={() => {
												setInfiniteScrollIndex(infiniteScrollIndex + 1)
												getResultatRecherche({ ...filtres, page: infiniteScrollIndex + 1 })
													.then((resultat: any) => {
														setResultat(resultat)
													})
											}}
											className={classes.infiniteScroll}
											// Toutes les offres dans le résultat ont le même total
											hasMore={resultat.offresExactes.length !== resultat.totalOffresExactes}
											loader={<Loader />}
											scrollThreshold={0.75}
										>
											{resultat.offresExactes.map((offreResultat, index) => (
												<Box
													key={`${offreResultat.sousTitre}-${offreResultat.dateDisponibilite}-${index}`}
													width={isLargeScreen ? 300 : '100%'}
													height="100%"
													paddingX={isLargeScreen ? '15px' : 0}
													paddingTop={isLargeScreen ? '23px' : '18px'}
													paddingBottom={isLargeScreen ? '30px' : '18px'}
													display="table-cell"
													onMouseEnter={() => setOffreSurvolee(offreResultat.alias)}
													onMouseLeave={() => setOffreSurvolee(null)}
												>
													<AutreElementCrossSellingCard
														elementCrossSelling={formatOffreResultatRecherche(offreResultat, intl, isLargeScreen ? 261 : 328, isLargeScreen ? 207 : 260)}
													/>
												</Box>
											))}
										</InfiniteScroll>
									</Grid>
							}
							{resultat.offresExactes.length < 8 && resultat.offresApprochantes.length > 0 && (
								// Résultats approchants
								<Grid container item xs={12}>
									{/* Titre des résultats approchants */}
									<Grid item xs={12} className={classes.resultatsApprochantsTitre}>
										<FormattedMessage
											id="recherche.resultatsApprochants.titre"
											defaultMessage="Ces <span>biens<span> pourraient aussi vous intéresser"
											description="Titre des résultats approchants"
											values={{ span: (chunk) => <span className={classes.colorAccessibility}>{chunk}</span> }}
										/>
									</Grid>

									{/* Listing des offres approchantes */}
									<AutresElementsCrossSelling
										elementsCrossSelling={offresApprochantes}
										width={300}
										height="100%"
										spaceBetweenSlides={isLargeScreen ? 36 : 24}
										doitMaj={afficherCarte}
									/>
								</Grid>
							)}
						</Grid>

						{/* //Sur Sitel, on ne mets pas de carte, à décommenter en cas d'évol + repasser sur les composants liés */}
						{/*Bouton sticky "Voir la carte" / "Voir la liste"*/}
						{/*<Button*/}
						{/*	background="backgroundAccessibility"*/}
						{/*	color="colorWhite"*/}
						{/*	textStyle="text4"*/}
						{/*	buttonStyle={classes.boutonSticky}*/}
						{/*	onClick={() => {*/}
						{/*		// Si on est en mobile, on scroll en haut de la page pour afficher la carte*/}
						{/*		if (!isLargeScreen) {*/}
						{/*			scroll.scrollToTop()*/}
						{/*		}*/}
						{/*		setAfficherCarte((prevState) => !prevState)*/}
						{/*		setMapCharge(false)*/}
						{/*	}}*/}
						{/*>*/}
						{/*	{*/}
						{/*		afficherCarte ? (*/}
						{/*			<>*/}
						{/*				<FormatListBulletedOutlined />*/}

						{/*				<FormattedMessage*/}
						{/*					id="recherche.afficherListe"*/}
						{/*					defaultMessage="Afficher la liste"*/}
						{/*					description="Bouton pour afficher la liste"*/}
						{/*				/>*/}
						{/*			</>*/}
						{/*		) : (*/}
						{/*			<>*/}
						{/*				<MapOutlined />*/}

						{/*				<FormattedMessage*/}
						{/*					id="recherche.afficherCarte"*/}
						{/*					defaultMessage="Afficher la carte"*/}
						{/*					description="Bouton pour afficher la carte"*/}
						{/*				/>*/}
						{/*			</>*/}
						{/*		)*/}
						{/*	}*/}
						{/*</Button>*/}

						{/*<Grid container item xs={12} className={classes.voirPlus}></Grid>*/}
					</Grid>
				)}

			{/*{*/}
			{/*	// Si on doit afficher la carte, on l'affiche en plein écran pour mobile / moitié pour desktop*/}
			{/*	afficherCarte && (*/}
			{/*		<Grid*/}
			{/*			container*/}
			{/*			item*/}
			{/*			xs={12}*/}
			{/*			md={6}*/}
			{/*			justifyContent="center"*/}
			{/*		>*/}
			{/*			/!* On affiche un loader le temps de charger la map *!/*/}
			{/*			<div*/}
			{/*				style={{ position: 'relative', zIndex: mapCharge ? -1 : 1 }}*/}
			{/*			>*/}
			{/*				<Loader />*/}
			{/*			</div>*/}

			{/*			/!* On masque le loader et on affiche la map une fois celle-ci chargée *!/*/}
			{/*			<div*/}
			{/*				className={*/}
			{/*					cx(*/}
			{/*						isLargeScreen ? classes.mapSticky : classes.mapAbsolute,*/}
			{/*						{*/}
			{/*							'no-overflow': !isLargeScreen*/}
			{/*						}*/}
			{/*					)*/}
			{/*				}*/}
			{/*				style={{ zIndex: mapCharge ? isLargeScreen ? 1 : 3 : -1 }}*/}
			{/*			>*/}
			{/*				<MapAlentoor*/}
			{/*					resultat={resultat}*/}
			{/*					auChargementMap={() => setMapCharge(true)}*/}
			{/*				/>*/}

			{/*				{*/}
			{/*					!isLargeScreen && (*/}
			{/*						// Bouton sticky "Voir la carte" / "Voir la liste"*/}
			{/*						<Button*/}
			{/*							background="backgroundAccessibility"*/}
			{/*							color="colorWhite"*/}
			{/*							textStyle="text4"*/}
			{/*							buttonStyle={classes.boutonSticky}*/}
			{/*							onClick={() => {*/}
			{/*								setAfficherCarte((prevState) => !prevState)*/}
			{/*								setMapCharge(false)*/}
			{/*							}}*/}
			{/*						>*/}
			{/*							<FormatListBulletedOutlined />*/}

			{/*							<FormattedMessage*/}
			{/*								id="recherche.afficherListe"*/}
			{/*								defaultMessage="Afficher la liste"*/}
			{/*								description="Bouton pour afficher la liste"*/}
			{/*							/>*/}
			{/*						</Button>*/}
			{/*					)*/}
			{/*				}*/}
			{/*			</div>*/}
			{/*		</Grid>*/}
			{/*	)*/}
			{/*}*/}
		</Grid>
	)
}

export default ResultatsRecherche